import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { bookingApi, PublicLinkInfo, AvailabilityDay } from '../services/api';
import { DateTimeSelector, SelectedSlot, AvailabilitySlot } from '../components/DateTimeSelector';
import { useEmbedMode } from '../hooks/useEmbedMode';
import {
    Calendar,
    Clock,
    User,
    Mail,
    MessageSquare,
    Star,
    X,
    Check,
    ChevronRight,
    ChevronLeft,
    Send
} from 'lucide-react';

export function PublicBookingPage() {
    const { slug } = useParams<{ slug: string }>();
    const { isEmbedded, notifyParentHeight } = useEmbedMode();

    // State
    const [link, setLink] = useState<PublicLinkInfo | null>(null);
    const [availability, setAvailability] = useState<AvailabilityDay[]>([]);
    const [timezone, setTimezone] = useState('America/New_York');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Date/time selection state
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedSlots, setSelectedSlots] = useState<SelectedSlot[]>([]);
    
    // Form state
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Step state (1 = select times, 2 = enter info)
    const [step, setStep] = useState(1);

    // Detect user's timezone
    useEffect(() => {
        try {
            const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const commonTimezones = [
                'America/New_York', 'America/Chicago', 'America/Denver', 
                'America/Los_Angeles', 'America/Phoenix', 'UTC',
                'Europe/London', 'Europe/Paris', 'Asia/Tokyo', 'Australia/Sydney'
            ];
            if (commonTimezones.includes(detected)) {
                setTimezone(detected);
            }
        } catch {
            // Keep default
        }
    }, []);

    // Notify parent of height changes when step or content changes (for embed mode)
    useEffect(() => {
        notifyParentHeight();
    }, [step, selectedSlots.length, isLoading, submitted, notifyParentHeight]);

    // Load booking link data
    useEffect(() => {
        if (!slug) return;
        loadLink();
    }, [slug, timezone]);

    const loadLink = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await bookingApi.getLink(slug!, timezone);
            setLink(data.link);
            setAvailability(data.availability);
            
            // Auto-select first available date
            const firstAvailableDay = data.availability.find(d => d.slots.length > 0);
            if (firstAvailableDay && !selectedDate) {
                setSelectedDate(firstAvailableDay.date);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load booking page');
        } finally {
            setIsLoading(false);
        }
    };

    const handleTimezoneChange = (newTz: string) => {
        setTimezone(newTz);
        setSelectedSlots([]); // Clear selections when timezone changes
        setSelectedDate(null); // Reset date so auto-select can run with new availability data
    };

    const handleSlotToggle = (slot: AvailabilitySlot, date: string, dayName: string) => {
        const slotWithDate: SelectedSlot = {
            ...slot,
            date,
            day_name: dayName
        };

        setSelectedSlots(prev => {
            const exists = prev.some(s => s.start_time_utc === slot.start_time_utc);
            
            if (exists) {
                return prev.filter(s => s.start_time_utc !== slot.start_time_utc);
            }
            
            // For direct booking, replace the selection (only one slot allowed)
            if (link?.booking_type === 'direct') {
                return [slotWithDate];
            }
            
            // Check max proposals limit
            if (link && prev.length >= link.max_proposals) {
                alert(`You can only select up to ${link.max_proposals} time preferences`);
                return prev;
            }
            
            return [...prev, slotWithDate];
        });
    };

    const removeSlot = (index: number) => {
        setSelectedSlots(prev => prev.filter((_, i) => i !== index));
    };

    const moveSlot = (from: number, to: number) => {
        if (to < 0 || to >= selectedSlots.length) return;
        setSelectedSlots(prev => {
            const result = [...prev];
            const [removed] = result.splice(from, 1);
            result.splice(to, 0, removed);
            return result;
        });
    };

    const isDirectBooking = link?.booking_type === 'direct';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim() || !email.trim()) {
            alert('Please enter your name and email');
            return;
        }

        if (selectedSlots.length === 0) {
            alert(isDirectBooking ? 'Please select a time' : 'Please select at least one time preference');
            return;
        }

        if (!slug) return;

        setIsSubmitting(true);
        try {
            if (isDirectBooking) {
                // Direct booking - instantly book the selected slot
                await bookingApi.directBook(slug, {
                    requester_name: name.trim(),
                    requester_email: email.trim(),
                    message: message.trim() || undefined,
                    slot: {
                        start_time_utc: selectedSlots[0].start_time_utc,
                        end_time_utc: selectedSlots[0].end_time_utc
                    },
                    timezone
                });
            } else {
                // Proposal booking - submit time preferences for review
                await bookingApi.submit(slug, {
                    requester_name: name.trim(),
                    requester_email: email.trim(),
                    message: message.trim() || undefined,
                    slots: selectedSlots.map(s => ({
                        start_time_utc: s.start_time_utc,
                        end_time_utc: s.end_time_utc
                    })),
                    timezone
                });
            }
            setSubmitted(true);
        } catch (err: any) {
            alert(err.message || (isDirectBooking ? 'Failed to book meeting' : 'Failed to submit request'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="booking-page" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh'
            }}>
                <div className="spinner" />
            </div>
        );
    }

    if (error || !link) {
        return (
            <div className="booking-page" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: 'var(--space-5)'
            }}>
                <div className="booking-card" style={{ maxWidth: '400px', textAlign: 'center' }}>
                    <Calendar size={48} style={{ color: '#666', margin: '0 auto var(--space-4)' }} />
                    <h2 style={{ color: 'var(--color-error)', marginBottom: 'var(--space-4)' }}>
                        Booking Unavailable
                    </h2>
                    <p style={{ color: '#888' }}>
                        {error || 'This booking link is no longer active.'}
                    </p>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="booking-page" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: 'var(--space-5)'
            }}>
                <div className="booking-card" style={{ maxWidth: '500px', textAlign: 'center' }}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: 'rgba(74, 222, 128, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto var(--space-4)'
                    }}>
                        <Check size={32} style={{ color: '#4ade80' }} />
                    </div>
                    <h2 style={{ marginBottom: 'var(--space-2)' }}>
                        {isDirectBooking ? 'Meeting Booked!' : 'Request Submitted!'}
                    </h2>
                    <p style={{ color: '#888', marginBottom: 'var(--space-4)' }}>
                        {isDirectBooking ? (
                            <>
                                Your meeting with <strong style={{ color: '#fff' }}>{link.user_name}</strong> has been confirmed. 
                                You'll receive a calendar invitation shortly.
                            </>
                        ) : (
                            <>
                                Your meeting request has been sent to <strong style={{ color: '#fff' }}>{link.user_name}</strong>. 
                                You'll receive an email confirmation once they review your proposed times.
                            </>
                        )}
                    </p>
                    <div style={{ 
                        background: '#2a2a2a', 
                        padding: 'var(--space-4)', 
                        borderRadius: 'var(--radius-md)',
                        textAlign: 'left'
                    }}>
                        <h4 style={{ marginBottom: 'var(--space-2)', color: '#fff' }}>
                            {isDirectBooking ? 'Confirmed time:' : 'Your proposed times:'}
                        </h4>
                        {selectedSlots.map((slot, index) => (
                            <div key={slot.start_time_utc} style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 'var(--space-2)',
                                padding: 'var(--space-1) 0',
                                fontSize: 'var(--font-size-sm)',
                                color: '#ccc'
                            }}>
                                {!isDirectBooking && index === 0 && <Star size={14} style={{ color: 'var(--color-primary)' }} />}
                                {!isDirectBooking && <span style={{ color: '#888', minWidth: '20px' }}>#{index + 1}</span>}
                                <span>{slot.day_name}, {slot.date} at {slot.start_time_local}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="booking-page" style={{ paddingBottom: isEmbedded ? 'var(--space-4)' : 'var(--space-8)' }}>
            {/* Header - hidden in embed mode */}
            <div className="embed-hide" style={{
                background: 'rgba(255,255,255,0.05)',
                padding: 'var(--space-5)',
                marginBottom: 'var(--space-4)'
            }}>
                <div className="container" style={{ maxWidth: '1100px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white' }}>
                        <Calendar size={24} />
                        <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                            AAM <span style={{ color: 'var(--color-primary)' }}>Let's Meet</span>
                        </span>
                    </div>
                </div>
            </div>

            <div className="container" style={{ maxWidth: '1100px' }}>
                {/* Link Info Card */}
                <div className="booking-card" style={{ marginBottom: 'var(--space-4)' }}>
                    <h1 style={{ marginBottom: 'var(--space-2)' }}>{link.name}</h1>
                    <p style={{ color: '#888', marginBottom: 'var(--space-3)' }}>
                        Schedule a meeting with <strong style={{ color: '#fff' }}>{link.user_name}</strong>
                    </p>
                    
                    {link.description && (
                        <p style={{ marginBottom: 'var(--space-4)', color: '#ccc' }}>{link.description}</p>
                    )}

                    <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#888' }}>
                            <Clock size={16} />
                            {link.duration_minutes} minutes
                        </span>
                        {!isDirectBooking && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#888' }}>
                                <Star size={16} />
                                Select up to {link.max_proposals} time{link.max_proposals > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                </div>

                {/* Step indicator */}
                <div style={{ 
                    display: 'flex', 
                    gap: 'var(--space-4)', 
                    marginBottom: 'var(--space-4)',
                    color: '#888',
                    fontSize: 'var(--font-size-sm)'
                }}>
                    <span style={{ 
                        color: step >= 1 ? 'var(--color-primary)' : '#888',
                        fontWeight: step === 1 ? 600 : 400
                    }}>
                        1. Select Times
                    </span>
                    <span>→</span>
                    <span style={{ 
                        color: step >= 2 ? 'var(--color-primary)' : '#888',
                        fontWeight: step === 2 ? 600 : 400
                    }}>
                        2. Your Information
                    </span>
                </div>

                {step === 1 && (
                    <div className="booking-layout">
                        {/* Calendar & Time Selection */}
                        <div>
                            {availability.length === 0 ? (
                                <div className="booking-card" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                                    <Calendar size={48} style={{ color: '#666', margin: '0 auto var(--space-4)' }} />
                                    <p style={{ color: '#888' }}>No availability in the selected time range.</p>
                                </div>
                            ) : (
                                <DateTimeSelector
                                    availableDays={availability}
                                    selectedDate={selectedDate}
                                    onDateChange={setSelectedDate}
                                    selectedSlots={selectedSlots}
                                    onSlotToggle={handleSlotToggle}
                                    multiSelect={!isDirectBooking && link.max_proposals > 1}
                                    maxSelections={isDirectBooking ? 1 : link.max_proposals}
                                    timezone={timezone}
                                    onTimezoneChange={handleTimezoneChange}
                                />
                            )}
                        </div>

                        {/* Selected Times Panel */}
                        <div className="selected-slots-panel">
                            <h4>
                                <Star size={18} style={{ color: 'var(--color-primary)' }} />
                                {isDirectBooking ? 'Selected Time' : 'Your Preferences'}
                                {!isDirectBooking && (
                                    <span style={{ 
                                        fontSize: 'var(--font-size-xs)', 
                                        color: '#888',
                                        fontWeight: 'normal',
                                        marginLeft: 'var(--space-2)'
                                    }}>
                                        ({selectedSlots.length}/{link.max_proposals})
                                    </span>
                                )}
                            </h4>

                            {selectedSlots.length === 0 ? (
                                <p style={{ color: '#666', fontSize: 'var(--font-size-sm)' }}>
                                    {isDirectBooking 
                                        ? 'Click on an available time to select it' 
                                        : 'Click on available times to select your preferences'}
                                </p>
                            ) : (
                                <div>
                                    {selectedSlots.map((slot, index) => (
                                        <div
                                            key={slot.start_time_utc}
                                            className={`selected-slot-item ${index === 0 ? 'primary' : ''}`}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                {/* Reordering controls - only for proposal mode with multiple slots */}
                                                {!isDirectBooking && selectedSlots.length > 1 && (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                        <button
                                                            onClick={() => moveSlot(index, index - 1)}
                                                            disabled={index === 0}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: index === 0 ? 'not-allowed' : 'pointer',
                                                                padding: 0,
                                                                opacity: index === 0 ? 0.3 : 1,
                                                                color: '#888'
                                                            }}
                                                        >
                                                            <ChevronLeft size={12} style={{ transform: 'rotate(90deg)' }} />
                                                        </button>
                                                        <button
                                                            onClick={() => moveSlot(index, index + 1)}
                                                            disabled={index === selectedSlots.length - 1}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: index === selectedSlots.length - 1 ? 'not-allowed' : 'pointer',
                                                                padding: 0,
                                                                opacity: index === selectedSlots.length - 1 ? 0.3 : 1,
                                                                color: '#888'
                                                            }}
                                                        >
                                                            <ChevronLeft size={12} style={{ transform: 'rotate(-90deg)' }} />
                                                        </button>
                                                    </div>
                                                )}
                                                
                                                {/* Order indicator - only for proposal mode */}
                                                {!isDirectBooking && (
                                                    <div className="selected-slot-order">
                                                        {index === 0 && <Star size={12} style={{ color: 'var(--color-primary)' }} />}
                                                        <span>#{index + 1}</span>
                                                    </div>
                                                )}
                                                
                                                <div style={{ color: '#ccc' }}>
                                                    <div style={{ fontSize: 'var(--font-size-xs)', color: '#888' }}>
                                                        {slot.day_name} {slot.date}
                                                    </div>
                                                    <div>{slot.start_time_local}</div>
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => removeSlot(index)}
                                                className="selected-slot-remove"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Next button */}
                            <button
                                onClick={() => setStep(2)}
                                disabled={selectedSlots.length === 0}
                                className="next-btn"
                            >
                                Next
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                        <button
                            onClick={() => setStep(1)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--color-primary)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginBottom: 'var(--space-4)',
                                fontSize: 'var(--font-size-sm)'
                            }}
                        >
                            <ChevronLeft size={16} />
                            Back to time selection
                        </button>

                        {/* Selected times summary */}
                        <div className="booking-card" style={{ marginBottom: 'var(--space-4)' }}>
                            <h4 style={{ marginBottom: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <Star size={16} style={{ color: 'var(--color-primary)' }} />
                                {isDirectBooking ? 'Selected Time' : 'Selected Times'}
                            </h4>
                            {selectedSlots.map((slot, index) => (
                                <div key={slot.start_time_utc} style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-2) 0',
                                    fontSize: 'var(--font-size-sm)',
                                    borderBottom: index < selectedSlots.length - 1 ? '1px solid #3a3a3a' : 'none',
                                    color: '#ccc'
                                }}>
                                    {!isDirectBooking && index === 0 && <Star size={12} style={{ color: 'var(--color-primary)' }} />}
                                    {!isDirectBooking && <span style={{ color: '#888', minWidth: '20px' }}>#{index + 1}</span>}
                                    <span>{slot.day_name}, {slot.date} at {slot.start_time_local}</span>
                                </div>
                            ))}
                        </div>

                        {/* Contact Form */}
                        <div className="booking-card">
                            <h3 style={{ marginBottom: 'var(--space-4)' }}>Your Information</h3>

                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <User size={14} />
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Your name"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Mail size={14} />
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        className="form-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="your@email.com"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <MessageSquare size={14} />
                                        Message (optional)
                                    </label>
                                    <textarea
                                        className="form-textarea"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="What would you like to discuss?"
                                        rows={3}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting || selectedSlots.length === 0}
                                    className="btn btn-primary btn-lg"
                                    style={{ width: '100%' }}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="spinner" style={{ width: '18px', height: '18px' }} />
                                            {isDirectBooking ? 'Booking...' : 'Submitting...'}
                                        </>
                                    ) : (
                                        <>
                                            <Send size={18} />
                                            {isDirectBooking ? 'Book Meeting' : 'Request Meeting'}
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
